import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { getArtists, getArtist } from '../services/adminService';
import { paymentLinkProvider } from '../utils/paymentLink';
import ImageUploadField from './ImageUploadField';
import ColorVariantsField from './ColorVariantsField';

const BUCKET = 'products_images';

const inputStyle = {
  width: '100%',
  padding: '0.55rem 0.75rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  fontSize: '0.9rem',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.82rem',
  fontWeight: '600',
  color: '#555',
  marginBottom: '0.3rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const selectStyle = {
  ...inputStyle,
  background: 'white',
};

const fieldStyle = { marginBottom: '1.1rem' };

const Field = ({ label, required, children }) => (
  <div style={fieldStyle}>
    <label style={labelStyle}>
      {label}{required && <span style={{ color: '#E4405F', marginLeft: 2 }}>*</span>}
    </label>
    {children}
  </div>
);

const EMPTY = {
  name: '',
  name_en: '',
  artist_id: '',
  paymentLinkUrl: '',
  artist: '',
  brand: '',
  brand_id: '',
  category_id: '',
  price: '',
  originalPrice: '',
  preorderPrice: '',
  quantity: '0',
  isPreorder: false,
  isSoldOut: false,
  isHidden: false,
  isInTransit: false,
  displayOrder: '',
  image: '',
  imageBack: '',
  description: '',
  description_en: '',
  series: '',
  series_en: '',
  size: '',
  features: '',
  spec_dimensions: '',
  spec_material: '',
  spec_authenticity: '',
  colorVariants: [],
};

function toFormValues(product) {
  if (!product) return EMPTY;
  const specs = product.specifications || {};
  return {
    name:              product.name || '',
    name_en:           product.name_en || '',
    artist_id:         product.artist_id || '',
    paymentLinkUrl:    product.paymentLinkUrl || '',
    artist:            product.artist || '',
    brand:             product.brand || '',
    brand_id:          product.brand_id != null ? String(product.brand_id) : '',
    category_id:       product.category_id != null ? String(product.category_id) : '',
    price:             product.price != null ? String(product.price) : '',
    originalPrice:     product.originalPrice != null ? String(product.originalPrice) : '',
    preorderPrice:     product.preorderPrice != null ? String(product.preorderPrice) : '',
    quantity:          product.quantity != null ? String(product.quantity) : '0',
    isPreorder:        Boolean(product.isPreorder),
    isSoldOut:         Boolean(product.isSoldOut),
    isHidden:          Boolean(product.isHidden),
    isInTransit: Boolean(product.isInTransit),
    displayOrder:      product.displayOrder != null ? String(product.displayOrder) : '',
    image:             product.image || '',
    imageBack:         product.imageBack || '',
    description:       product.description || '',
    description_en:    product.description_en || '',
    series:            product.series || '',
    series_en:         product.series_en || '',
    size:              product.size || '',
    features:          Array.isArray(product.features) ? product.features.join(', ') : '',
    spec_dimensions:   specs.dimensions || '',
    spec_material:     specs.material || '',
    spec_authenticity: specs.authenticity || '',
    colorVariants:     Array.isArray(product.colorVariants) ? product.colorVariants : [],
  };
}

// ---------------------------------------------------------------------------
// AdminProductForm
// ---------------------------------------------------------------------------

/**
 * Controlled product form used by both create and edit pages.
 *
 * Props:
 *   initialValues {object|null}  — product data to pre-fill (null for new)
 *   onSubmit(data) {function}    — called with normalized form data
 *   loading {boolean}
 *   error   {string|null}
 */
const AdminProductForm = ({ initialValues = null, onSubmit, loading = false, error = null }) => {
  const { session } = useAuth();
  const [form, setForm] = useState(() => toFormValues(initialValues));
  const [imageUploading, setImageUploading] = useState(false);
  const [imageBackUploading, setImageBackUploading] = useState(false);
  const [artists, setArtists] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    setForm(toFormValues(initialValues));
  }, [initialValues]);

  useEffect(() => {
    supabase
      .from('categories')
      .select('id, name')
      .order('name')
      .then(({ data, error }) => {
        if (error) {
          console.error('[AdminProductForm] failed to load categories:', error);
          return;
        }
        if (data) setCategories(data);
      });
  }, []);

  useEffect(() => {
    supabase
      .from('brands')
      .select('id, name')
      .order('name')
      .then(({ data, error }) => {
        if (error) {
          console.error('[AdminProductForm] failed to load brands:', error);
          return;
        }
        if (data) setBrands(data);
      });
  }, []);

  useEffect(() => {
    const token = session?.access_token;
    if (!token) return;

    let cancelled = false;

    (async () => {
      try {
        // Admin endpoint returns every artist (no featured/verified filter) —
        // page through all results since the list dropdown needs the full set.
        const all = [];
        let page = 1;
        let totalPages = 1;
        do {
          const data = await getArtists(token, { page, limit: 100 });
          all.push(...(data.artists || []));
          totalPages = data.totalPages || 1;
          page += 1;
        } while (page <= totalPages);

        if (cancelled) return;
        // When an artist has priority (creatorAboutFirst), their own name is
        // what leads on the public site — mirror that in the picker too.
        const mapped = all
          .map((r) => ({
            id: r.id,
            name: (r.creatorAboutFirst && r.creatorName) ? r.creatorName : (r.name || ''),
            hasBankAccount: r.hasBankAccount,
          }))
          .filter((r) => r.id && r.name)
          .sort((a, b) => a.name.localeCompare(b.name));
        setArtists(mapped);
      } catch (err) {
        if (!cancelled) console.error('[AdminProductForm] failed to load artists:', err.message);
      }
    })();

    return () => { cancelled = true; };
  }, [session?.access_token]);

  // "Crear desde un custom" — lets staff turn a one-off piece from an
  // artist's customs/events timeline (artist_customs table, migration 074)
  // into a sellable product, so a custom shown on the artist page doesn't
  // have to be re-typed by hand when it's ready to sell. Only offered when
  // creating a new product: it snapshots values into the form (same
  // convention as the kiosk-loop video picker in AdminKioskLoopPage.js) —
  // there is no ongoing link to the source custom afterward.
  const isNewProduct = initialValues == null;
  const [customPickerArtistId, setCustomPickerArtistId] = useState('');
  const [artistCustoms, setArtistCustoms] = useState([]);
  const [customPickerEntryId, setCustomPickerEntryId] = useState('');
  const [loadingCustoms, setLoadingCustoms] = useState(false);

  const handlePickCustomArtist = async (artistId) => {
    setCustomPickerArtistId(artistId);
    setCustomPickerEntryId('');
    setArtistCustoms([]);
    if (!artistId || !session?.access_token) return;
    setLoadingCustoms(true);
    try {
      const data = await getArtist(session.access_token, artistId);
      setArtistCustoms(data.artist?.customShowHistory || []);
    } catch (err) {
      console.error('[AdminProductForm] failed to load artist customs:', err.message);
    } finally {
      setLoadingCustoms(false);
    }
  };

  const handlePickCustomEntry = (entryId) => {
    setCustomPickerEntryId(entryId);
    const entry = artistCustoms.find((c) => c.id === entryId);
    if (!entry) return;
    const artist = artists.find((a) => a.id === customPickerArtistId);
    setForm((prev) => ({
      ...prev,
      name: entry.customName || entry.eventName || prev.name,
      name_en: entry.customName_en || entry.eventName_en || '',
      artist_id: customPickerArtistId,
      artist: artist ? artist.name : prev.artist,
      price: entry.price != null ? String(entry.price) : prev.price,
      quantity: '1',
      isSoldOut: Boolean(entry.soldOut),
      image: entry.images?.[0] || prev.image,
      imageBack: entry.images?.[1] || '',
      description: entry.story || '',
      description_en: entry.story_en || '',
      series: entry.platform || '',
      series_en: entry.platform_en || '',
      size: entry.dimensions || '',
      spec_dimensions: entry.dimensions || '',
      spec_material: entry.materials || '',
    }));
  };

  const set = (field) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'quantity') {
        next.isSoldOut = parseInt(value) === 0 && !next.isPreorder;
      }
      return next;
    });
  };

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const featuresArr = form.features
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    const specifications = {};
    if (form.spec_dimensions.trim())   specifications.dimensions   = form.spec_dimensions.trim();
    if (form.spec_material.trim())     specifications.material     = form.spec_material.trim();
    if (form.spec_authenticity.trim()) specifications.authenticity = form.spec_authenticity.trim();

    const colorVariants = form.colorVariants
      .filter((v) => v.name.trim())
      .map((v) => ({
        id: v.id,
        name: v.name.trim(),
        name_en: v.name_en?.trim() || null,
        hex: v.hex?.trim() || null,
        image: v.image?.trim() || null,
        imageBack: v.imageBack?.trim() || null,
        quantity: Math.max(0, parseInt(v.quantity, 10) || 0),
      }));

    onSubmit({
      name:           form.name.trim(),
      name_en:        form.name_en.trim() || null,
      artist_id:      form.artist_id || null,
      artist:         form.artist.trim() || null,
      // Mercado Pago / Clip link the buyer pays the artist with (artist pieces only).
      paymentLinkUrl: form.artist_id ? (form.paymentLinkUrl.trim() || null) : null,
      // brand / category text are no longer sent — the server derives them
      // from the lookup row pointed to by brand_id / category_id.
      brand_id:       form.brand_id ? parseInt(form.brand_id, 10) : null,
      category_id:    form.category_id ? parseInt(form.category_id, 10) : null,
      price:          parseFloat(form.price),
      originalPrice:  form.originalPrice !== '' ? parseFloat(form.originalPrice) : null,
      preorderPrice:  form.isPreorder && form.preorderPrice ? parseFloat(form.preorderPrice) : null,
      quantity:       parseInt(form.quantity) || 0,
      isPreorder:     form.isPreorder,
      isSoldOut:      form.isSoldOut,
      isHidden:       form.isHidden,
      isInTransit: form.isInTransit,
      displayOrder:   form.displayOrder === '' ? null : parseInt(form.displayOrder, 10),
      image:          form.image.trim() || null,
      imageBack:      form.imageBack.trim() || null,
      description:    form.description.trim() || null,
      description_en: form.description_en.trim() || null,
      series:         form.series.trim() || null,
      series_en:      form.series_en.trim() || null,
      size:           form.size.trim() || null,
      features:       featuresArr,
      specifications: Object.keys(specifications).length ? specifications : null,
      color_variants: colorVariants,
    });
  };

  const [variantsUploading, setVariantsUploading] = useState(false);
  const isUploading = imageUploading || imageBackUploading || variantsUploading;
  const hasVariants = form.colorVariants.some((v) => v.name.trim());

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <p style={{ color: '#dc3545', fontSize: '0.9rem', marginBottom: '1.25rem' }}>{error}</p>
      )}

      {/* Create from an artist custom (artist_customs table) */}
      {isNewProduct && (
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '1.5rem', padding: '0.85rem 1rem', background: '#f0f7fc', borderRadius: '8px', border: '1px solid #d3e8f5' }}>
          <div style={{ flex: '1 1 220px' }}>
            <label style={labelStyle}>Crear desde un custom del artista</label>
            <select
              value={customPickerArtistId}
              onChange={(e) => handlePickCustomArtist(e.target.value)}
              style={{ ...selectStyle, cursor: 'pointer' }}
            >
              <option value="">— Selecciona un artista —</option>
              {artists.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>
          {customPickerArtistId && (
            <div style={{ flex: '1 1 260px' }}>
              <label style={labelStyle}>Custom</label>
              <select
                value={customPickerEntryId}
                onChange={(e) => handlePickCustomEntry(e.target.value)}
                disabled={loadingCustoms}
                style={{ ...selectStyle, cursor: loadingCustoms ? 'default' : 'pointer' }}
              >
                <option value="">
                  {loadingCustoms ? 'Cargando…' : artistCustoms.length === 0 ? 'Este artista no tiene customs' : '— Selecciona un custom —'}
                </option>
                {artistCustoms.map((entry) => (
                  <option key={entry.id} value={entry.id}>
                    {(entry.customName || entry.eventName)}{entry.year ? ` (${entry.year})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Basic info */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.25rem' }}>
        <Field label="Nombre" required>
          <input
            type="text"
            value={form.name}
            onChange={set('name')}
            required
            placeholder="Ej. KAWS Companion Open Edition"
            style={inputStyle}
          />
        </Field>

        <Field label="Artista">
          <select
            value={form.artist_id}
            onChange={(e) => {
              const selectedId = e.target.value;
              if (selectedId && !artists.find((a) => a.id === selectedId)) return;
              const selectedArtist = artists.find((a) => a.id === selectedId);
              setForm((prev) => ({
                ...prev,
                artist_id: selectedId,
                artist: selectedArtist ? selectedArtist.name : '',
              }));
            }}
            style={{ ...inputStyle, cursor: 'pointer', color: form.artist_id ? '#333' : '#999' }}
          >
            <option value="">— Ninguno —</option>
            {artists.map((a) => (
              <option key={a.id} value={a.id}>{a.name}{a.hasBankAccount === false ? ' (sin CLABE)' : ''}</option>
            ))}
          </select>
          {form.artist_id && artists.find((a) => a.id === form.artist_id)?.hasBankAccount === false && (
            <p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: '#856404' }}>
              Este artista no tiene CLABE: agrega sus datos bancarios en su perfil antes de subir piezas nuevas.
            </p>
          )}
        </Field>

        {form.artist_id && (
          <Field label="Link de pago (Mercado Pago o Clip)">
            <input
              type="url"
              value={form.paymentLinkUrl}
              onChange={(e) => setField('paymentLinkUrl', e.target.value)}
              placeholder="https://mpago.la/… o https://pago.clip.mx/…"
              style={inputStyle}
            />
            <p style={{ margin: '0.4rem 0 0', fontSize: '0.8rem', color: form.paymentLinkUrl.trim() && !paymentLinkProvider(form.paymentLinkUrl) ? '#dc3545' : '#888' }}>
              {form.paymentLinkUrl.trim() && !paymentLinkProvider(form.paymentLinkUrl)
                ? 'Pega una liga de pago de Mercado Pago o Clip.'
                : 'Opcional. Link del artista con el precio de la pieza — el comprador le paga directo. Si el artista tiene CLABE, la pieza ya no se cobra con tarjeta.'}
            </p>
          </Field>
        )}

        <Field label="Marca / Brand">
          <select
            value={form.brand_id}
            onChange={(e) => {
              const id = e.target.value;
              const selected = brands.find((b) => String(b.id) === id);
              // Only update brand text when a real selection is made; on
              // deselect, keep whatever text the product already had so legacy
              // / unbackfilled values aren't blanked on save.
              setForm((prev) => ({
                ...prev,
                brand_id: id,
                brand: selected ? selected.name : prev.brand,
              }));
            }}
            style={{ ...inputStyle, cursor: 'pointer', color: form.brand_id ? '#333' : '#999' }}
          >
            <option value="">— Sin marca —</option>
            {brands.map((b) => (
              <option key={b.id} value={String(b.id)}>{b.name}</option>
            ))}
            {/* Legacy fallback: if the product carries a brand text that isn't
                in the lookup yet, show it so the admin can see the current
                value instead of a misleading "— Sin marca —". */}
            {form.brand && !form.brand_id && (
              <option value="" disabled>↳ Texto actual: {form.brand}</option>
            )}
          </select>
        </Field>

        <Field label="Categoría">
          <select
            value={form.category_id}
            onChange={(e) => setField('category_id', e.target.value)}
            style={{ ...inputStyle, cursor: 'pointer', color: form.category_id ? '#333' : '#999' }}
          >
            <option value="">— Sin categoría —</option>
            {categories.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </select>
        </Field>

        <Field label="Orden de visualización (1 = primero)">
          <input
            type="number"
            value={form.displayOrder}
            onChange={set('displayOrder')}
            min="1"
            step="1"
            placeholder="Vacío = al final"
            style={inputStyle}
          />
        </Field>

        <Field label="Precio (MXN)" required>
          <input
            type="number"
            value={form.price}
            onChange={set('price')}
            required
            min="50"
            step="50"
            placeholder="0.00"
            style={inputStyle}
          />
        </Field>

        <Field label="Precio original / Costo (MXN) — solo admin">
          <input
            type="number"
            value={form.originalPrice}
            onChange={set('originalPrice')}
            min="0"
            step="0.01"
            placeholder="Ej. 800.00 (no visible para clientes)"
            style={{ ...inputStyle, borderColor: '#f0a500', background: '#fffdf0' }}
          />
        </Field>

        {form.isPreorder && (
          <Field label="Precio de preventa (depósito, MXN)">
            <input
              type="number"
              value={form.preorderPrice}
              onChange={set('preorderPrice')}
              min="0.01"
              step="0.01"
              placeholder="Ej. 500.00 (vacío = 50% del precio)"
              style={inputStyle}
            />
          </Field>
        )}

        <Field label={hasVariants ? 'Cantidad en stock (suma de variantes)' : 'Cantidad en stock'}>
          <input
            type="number"
            value={hasVariants
              ? form.colorVariants.reduce((sum, v) => sum + (parseInt(v.quantity, 10) || 0), 0)
              : form.quantity}
            onChange={set('quantity')}
            min="0"
            step="1"
            disabled={hasVariants}
            title={hasVariants ? 'Se calcula automáticamente a partir del stock de cada variante' : undefined}
            style={{ ...inputStyle, ...(hasVariants ? { background: '#f0f0f0', color: '#888' } : {}) }}
          />
        </Field>

        <Field label="Serie / Colección">
          <input type="text" value={form.series} onChange={set('series')} placeholder="Ej. BFF Series" style={inputStyle} />
        </Field>

        <Field label="Tamaño / Dimensiones">
          <input type="text" value={form.size} onChange={set('size')} placeholder="Ej. 28 cm" style={inputStyle} />
        </Field>
      </div>

      {/* Flags */}
      <div style={{ display: 'flex', gap: '2rem', marginBottom: '1.25rem' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.isPreorder} onChange={set('isPreorder')} />
          Preventa
        </label>
        <label
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: hasVariants ? 'default' : 'pointer', color: hasVariants ? '#999' : 'inherit' }}
          title={hasVariants ? 'Se calcula automáticamente: agotado cuando todas las variantes lo están' : undefined}
        >
          <input type="checkbox" checked={form.isSoldOut} onChange={set('isSoldOut')} disabled={hasVariants} />
          Agotado
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer', color: form.isHidden ? '#888' : 'inherit' }}>
          <input type="checkbox" checked={form.isHidden} onChange={set('isHidden')} />
          Oculto (no visible para clientes)
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={form.isInTransit} onChange={set('isInTransit')} />
          En camino (kiosko)
        </label>
      </div>

      {/* Image uploads */}
      <ImageUploadField
        label="Imagen principal"
        value={form.image}
        onChange={(url) => setField('image', url)}
        bucket={BUCKET}
        onUploadingChange={setImageUploading}
      />

      <ImageUploadField
        label="Imagen trasera"
        value={form.imageBack}
        onChange={(url) => setField('imageBack', url)}
        bucket={BUCKET}
        onUploadingChange={setImageBackUploading}
      />

      {/* Color variants */}
      <ColorVariantsField
        value={form.colorVariants}
        onChange={(next) => setField('colorVariants', next)}
        onUploadingChange={setVariantsUploading}
      />

      {/* Description */}
      <Field label="Descripción">
        <textarea
          value={form.description}
          onChange={set('description')}
          rows={4}
          placeholder="Descripción del producto..."
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </Field>

      {/* English translations (optional) — collapsed by default */}
      <details style={{ margin: '0 0 1.25rem', padding: '0.75rem 1rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
        <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: '0.95rem', color: '#333' }}>
          Traducciones (English) — opcional
        </summary>
        <p style={{ fontSize: '0.85rem', color: '#666', margin: '0.5rem 0 1rem' }}>
          Si los dejas vacíos, se traducen automáticamente al guardar (Claude Haiku).
          Para volver a generar la traducción, borra el campo y guarda de nuevo.
        </p>
        <Field label="Name (English)">
          <input
            type="text"
            value={form.name_en}
            onChange={set('name_en')}
            placeholder="e.g. KAWS Companion Open Edition"
            style={inputStyle}
          />
        </Field>
        <Field label="Series / Collection (English)">
          <input
            type="text"
            value={form.series_en}
            onChange={set('series_en')}
            placeholder="e.g. BFF Series"
            style={inputStyle}
          />
        </Field>
        <Field label="Description (English)">
          <textarea
            value={form.description_en}
            onChange={set('description_en')}
            rows={4}
            placeholder="Product description in English…"
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </Field>
      </details>

      {/* Features */}
      <Field label="Características (separadas por coma)">
        <input
          type="text"
          value={form.features}
          onChange={set('features')}
          placeholder="Ej. Edición limitada, Figura vinilo, Con caja original"
          style={inputStyle}
        />
      </Field>

      {/* Specifications */}
      <details style={{ marginBottom: '1.5rem' }}>
        <summary style={{ ...labelStyle, cursor: 'pointer', userSelect: 'none' }}>
          Especificaciones (opcional)
        </summary>
        <div style={{ paddingTop: '0.75rem', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.25rem' }}>
          <Field label="Dimensiones">
            <input type="text" value={form.spec_dimensions} onChange={set('spec_dimensions')} placeholder="Ej. 28 x 14 x 10 cm" style={inputStyle} />
          </Field>
          <Field label="Material">
            <input type="text" value={form.spec_material} onChange={set('spec_material')} placeholder="Ej. Vinilo" style={inputStyle} />
          </Field>
          <Field label="Autenticidad">
            <input type="text" value={form.spec_authenticity} onChange={set('spec_authenticity')} placeholder="Ej. Incluye certificado" style={inputStyle} />
          </Field>
        </div>
      </details>

      <button
        type="submit"
        disabled={loading || isUploading}
        style={{
          padding: '0.7rem 2rem',
          background: loading || isUploading ? '#ccc' : '#2ca9e1',
          color: 'white',
          border: 'none',
          borderRadius: '8px',
          fontSize: '0.95rem',
          fontWeight: '600',
          cursor: loading || isUploading ? 'default' : 'pointer',
        }}
      >
        {loading ? 'Guardando...' : isUploading ? 'Subiendo imagen...' : 'Guardar producto'}
      </button>
    </form>
  );
};

export default AdminProductForm;
